import { useState, useEffect, Fragment } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { resolveHomeSections } from "./homeSections";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Calendar } from "./components/ui/calendar";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import Shop from "./Shop";
import Atelier from "./Atelier";
import FileService from "./FileService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Wrench,
  Circle,
  Gauge,
  Calendar as CalendarIcon,
  MapPin,
  Phone,
  Clock,
  Mail,
  ChevronDown,
  ChevronRight,
  Check,
  Zap,
  Star,
  ExternalLink,
  MessageCircle,
  MessageSquare,
  Tag,
  X,
  Image,
  Menu,
} from "lucide-react";
import Admin from "./Admin";
import FlexfuelPage from "./FlexfuelPage";
import ReprogPage from "./ReprogPage";
import AchatVentePage from "./AchatVentePage";
import RepEntretienPage from "./RepEntretienPage";
import DepotVenteTech from "./DepotVenteTech";
import PrintableVehiclePage from "./PrintableVehiclePage";
import { useSiteMeta } from "./hooks/useSiteMeta";
import { useContactInfo } from "./hooks/useContactInfo";
import { useNavbarConfig } from "./hooks/useNavbarConfig";
import { ReviewNudgeToast } from "./components/ReviewNudgeToast";
import { StaticBanner } from "./components/StaticBanner";
import { VacationBanner } from "./components/VacationBanner";
import { AnimationEmbed } from "./components/AnimationEmbed";
// Extracted home sections
import { HeroSection } from "./components/sections/HeroSection";
import { PromotionsSection, GallerySection, VehicleSelectorWidget } from "./components/sections/PromotionsSection";
import { FlexfuelCalculator, FlexfuelFAQSection, ServicesSection } from "./components/sections/FlexfuelCalculator";
import { SEOEditorialSection } from "./components/sections/SEOEditorialSection";
import { BookingSection, MarqueeSection } from "./components/sections/BookingSection";
import { GoogleReviewsSection } from "./components/sections/GoogleReviewsSection";
import { TestimonialsSection } from "./components/sections/TestimonialsSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Check quote payment on page load
const checkQuotePayment = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const quoteId = urlParams.get('quote_id');
  const payment = urlParams.get('payment');
  const paymentType = urlParams.get('type');
  
  if (quoteId && payment === 'success') {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/check-payment`);
      if (response.data.status === 'deposit_paid' || response.data.status === 'fully_paid') {
        toast.success(
          paymentType === 'deposit' 
            ? `✅ Acompte de ${response.data.amount_paid?.toFixed(2)} € confirmé !` 
            : `✅ Paiement de ${response.data.amount_paid?.toFixed(2)} € confirmé !`
        );
      }
    } catch (error) {
      console.error("Error checking quote payment:", error);
    }
    // Clean URL
    window.history.replaceState({}, document.title, '/');
  } else if (payment === 'cancelled') {
    toast.error('Paiement annulé');
    window.history.replaceState({}, document.title, '/');
  }
};

// WhatsApp number (France: +33)
const WHATSAPP_NUMBER = "33649070820";
const WHATSAPP_MESSAGE = "Bonjour, je souhaite prendre rendez-vous pour un service automobile.";

// Social media links
const INSTAGRAM_URL = "https://www.instagram.com/auvergne_tech_racing";
const FACEBOOK_URL = "https://www.facebook.com/auvergnetechracing";

// Icon mapping
const IconMap = {
  wrench: Wrench,
  circle: Circle,
  gauge: Gauge,
  zap: Zap,
  messageSquare: MessageSquare,
};

export { IconMap };

// ============== SOCIAL MEDIA FLOATING BUTTONS ==============
const SocialMediaButtons = () => {
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
  
  return (
    <div className="fixed bottom-6 left-6 z-50 flex flex-col gap-3">
      {/* WhatsApp */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-button"
        className="bg-[#25D366] hover:bg-[#20BA5C] text-white p-3 rounded-full shadow-lg hover:scale-110 transition-all duration-300 hover:shadow-[0_0_20px_rgba(37,211,102,0.5)]"
        aria-label="Contacter sur WhatsApp"
      >
        <svg 
          viewBox="0 0 24 24" 
          className="w-6 h-6 fill-current"
          aria-hidden="true"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* Instagram */}
      <a
        href={INSTAGRAM_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="instagram-button"
        className="bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 text-white p-3 rounded-full shadow-lg hover:scale-110 transition-all duration-300 hover:shadow-[0_0_20px_rgba(221,42,123,0.5)]"
        aria-label="Suivre sur Instagram"
      >
        <svg 
          viewBox="0 0 24 24" 
          className="w-6 h-6 fill-current"
          aria-hidden="true"
        >
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      </a>

      {/* Facebook */}
      <a
        href={FACEBOOK_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="facebook-button"
        className="bg-[#1877F2] hover:bg-[#166FE5] text-white p-3 rounded-full shadow-lg hover:scale-110 transition-all duration-300 hover:shadow-[0_0_20px_rgba(24,119,242,0.5)]"
        aria-label="Suivre sur Facebook"
      >
        <svg 
          viewBox="0 0 24 24" 
          className="w-6 h-6 fill-current"
          aria-hidden="true"
        >
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </a>
    </div>
  );
};

// ============== PROMO BANNER ==============
const PromoBanner = ({ promotions, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const bannerPromos = promotions.filter(p => p.show_banner && p.is_active);
  
  useEffect(() => {
    if (bannerPromos.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % bannerPromos.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [bannerPromos.length]);
  
  if (bannerPromos.length === 0) return null;
  
  const currentPromo = bannerPromos[currentIndex];
  
  // Truncate description for banner (max 60 characters)
  const truncatedDesc = currentPromo.description?.length > 60 
    ? currentPromo.description.substring(0, 60) + "..." 
    : currentPromo.description;

  const scrollToPromos = () => {
    document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
  };
  
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-[#FF3B30] to-[#FF6B30] text-white h-[28px] flex items-center px-4 text-sm" data-testid="promo-banner">
      <div 
        onClick={scrollToPromos}
        className="max-w-7xl mx-auto flex items-center justify-center gap-3 w-full cursor-pointer hover:opacity-90 transition-opacity"
      >
        <Tag size={14} className="flex-shrink-0" />
        <div className="text-center truncate">
          <span className="font-bold text-sm">{currentPromo.title}</span>
          {currentPromo.discount && (
            <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs">{currentPromo.discount}</span>
          )}
          <span className="ml-2 text-white/90 text-xs hidden md:inline">{truncatedDesc}</span>
        </div>
        {bannerPromos.length > 1 && (
          <div className="flex gap-1 flex-shrink-0">
            {bannerPromos.map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === currentIndex ? 'bg-white' : 'bg-white/40'}`} />
            ))}
          </div>
        )}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-white/20 rounded transition-colors"
        aria-label="Fermer"
      >
        <X size={14} />
      </button>
    </div>
  );
};

// ============== PROMOTIONS SECTION ==============
// ============== FOOTER ==============
export const Footer = ({ businessHours, content }) => {
  // The Footer prefers content.footer if the admin has explicitly set values in
  // home_landing.footer. Otherwise it falls back to the centralized contact_info
  // CMS so a single admin edit propagates everywhere.
  const contact = useContactInfo();
  const footer = content?.footer || {
    address: contact.fullAddress,
    phone: contact.phone_display,
    email: contact.email,
  };
  // Parse multi-line address (accept comma or newline)
  const addressLines = String(footer.address || "").split(/,\s*|\n/).filter(Boolean);
  const phoneDigits = String(footer.phone || "").replace(/\s+/g, "");
  return (
    <footer id="contact" className="bg-[#0A0A0A] border-t border-white/10 py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Logo & Info */}
          <div>
            <img 
              src={LOGO_DARK} 
              alt="ATR Garage" 
              className="h-14 w-auto mb-4"
            />
            <p className="text-white/60 text-sm leading-relaxed">
              Services automobiles professionnels à Clermont-Ferrand. Expertise,
              rapidité et qualité garanties.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-6">
              Contact
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[#FF3B30] mt-1 flex-shrink-0" />
                <p className="text-white/70 text-sm">
                  {addressLines.map((line, i) => (
                    <span key={i}>
                      {line}
                      {i < addressLines.length - 1 && <br />}
                    </span>
                  ))}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-[#FF3B30] flex-shrink-0" />
                <a
                  href={`tel:${phoneDigits}`}
                  className="text-white/70 text-sm hover:text-[#FF3B30] transition-colors"
                >
                  {footer.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-[#FF3B30] flex-shrink-0" />
                <a
                  href={`mailto:${footer.email}`}
                  className="text-white/70 text-sm hover:text-[#FF3B30] transition-colors"
                >
                  {footer.email}
                </a>
              </div>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-6">
              Horaires
            </h4>
            <div className="space-y-2">
              {businessHours.map((hours) => (
                <div
                  key={hours.day}
                  className="flex justify-between text-sm"
                >
                  <span className="text-white/70">{hours.day}</span>
                  <span
                    className={
                      hours.is_open ? "text-white" : "text-white/40"
                    }
                  >
                    {hours.is_open
                      ? `${hours.open} - ${hours.close}`
                      : "Fermé"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Auvergne Tech Racing. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Logo URLs
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_67cb5430-fb0d-4c25-a9f8-a4cee239dafe/artifacts/1vm4vdax_IMG_1412.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_67cb5430-fb0d-4c25-a9f8-a4cee239dafe/artifacts/ryyd1nm8_IMG_2029.jpeg";

const DEFAULT_NAVBAR_ITEMS = [
  { id: "services-group", label: "Services", type: "group", href: "", style: "normal", visible: true },
  { id: "flexfuel", label: "Flexfuel", href: "/conversion-flexfuel-e85", parent_id: "services-group", style: "highlight", visible: true },
  { id: "optimisation", label: "Optimisation", href: "/reprogrammation-moteur-clermont-ferrand", parent_id: "services-group", style: "highlight", visible: true },
  { id: "depot-vente", label: "Dépôt-Vente", href: "/achat-vente-voiture-clermont-ferrand", parent_id: "services-group", style: "highlight", visible: true },
  { id: "mecanique", label: "Mécanique", href: "/reparation-entretien-clermont-ferrand", parent_id: "services-group", style: "highlight", visible: true },
  { id: "vehicule", label: "Véhicule", href: "/#vehicle-selector", style: "normal", visible: true },
  { id: "reservation", label: "Réservation", href: "/#booking", style: "normal", visible: true },
  { id: "boutique", label: "Boutique", href: "/shop", style: "normal", visible: true },
  { id: "file-service", label: "Service Fichiers", href: "/file-service", style: "normal", visible: true },
  { id: "contact", label: "Contact", href: "/#contact", style: "normal", visible: true },
];

const navLinkClass = (style) =>
  style === "highlight"
    ? "text-[#FF3B30] hover:text-[#ff6b62] text-sm font-semibold tracking-wide transition-colors"
    : "text-white/70 hover:text-white text-sm font-medium tracking-wide transition-colors";

const mobileNavLinkClass = (style) =>
  style === "highlight"
    ? "block text-[#FF3B30] hover:text-[#ff6b62] text-sm font-semibold tracking-wide transition-colors py-2"
    : "block text-white/70 hover:text-white text-sm font-medium tracking-wide transition-colors py-2";

// Dropdown component for navbar groups (hover on desktop)
const NavbarDropdown = ({ group, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`navbar-group-${group.id}`}
    >
      <button
        type="button"
        className={`${navLinkClass(group.style)} inline-flex items-center gap-1`}
        onClick={() => setOpen((v) => !v)}
      >
        {group.label}
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 min-w-[220px] z-50">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-lg shadow-2xl py-2 overflow-hidden">
            {children.map((child) => (
              <a
                key={child.id}
                href={child.href}
                data-testid={`navbar-${child.id}`}
                className={`block px-5 py-2.5 text-sm ${
                  child.style === "highlight"
                    ? "text-[#FF3B30] hover:bg-[#FF3B30]/10 font-semibold"
                    : "text-white/80 hover:bg-white/[0.06] font-medium"
                } transition-colors`}
              >
                {child.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============== NAVBAR ==============
export const Navbar = ({ onBookClick, hasBanner = false, onSiteBannerChange }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [staticBannerOn, setStaticBannerOn] = useState(false);
  const navConfig = useNavbarConfig(DEFAULT_NAVBAR_ITEMS);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const visibleItems = (navConfig.items && navConfig.items.length > 0 ? navConfig.items : DEFAULT_NAVBAR_ITEMS).filter((i) => i.visible !== false);
  // Build render tree: top-level items + children lookup for groups
  const topLevelItems = visibleItems.filter((i) => !i.parent_id);
  const childrenOf = (parentId) => visibleItems.filter((i) => i.parent_id === parentId);

  return (
    <>
    <StaticBanner onHeightChange={setStaticBannerOn} onEnabledChange={onSiteBannerChange} />
    <nav
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        hasBanner || staticBannerOn ? 'top-[28px]' : 'top-0'
      } ${
        scrolled
          ? "bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <VacationBanner />
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <a
          href="/"
          data-testid="navbar-logo"
          className="flex items-center"
        >
          <img
            src={LOGO_DARK}
            alt="Auvergne Tech Racing"
            className="h-10 w-auto"
          />
        </a>

        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {topLevelItems.map((item) =>
            item.type === "group" ? (
              <NavbarDropdown key={item.id} group={item} children={childrenOf(item.id)} />
            ) : (
              <a
                key={item.id}
                href={item.href}
                data-testid={`navbar-${item.id}`}
                className={navLinkClass(item.style)}
              >
                {item.label}
              </a>
            )
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onBookClick}
            data-testid="navbar-book-btn"
            className="bg-[#FF3B30] hover:bg-[#D63025] text-white text-sm font-bold uppercase tracking-wider px-6 py-2 transform skew-x-[-10deg] transition-all duration-300"
          >
            <span className="skew-content inline-block">{navConfig.book_button_label || "RDV"}</span>
          </button>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-md border-t border-white/10">
          <div className="px-4 py-4 space-y-3">
            {topLevelItems.map((item) => {
              if (item.type === "group") {
                const children = childrenOf(item.id);
                return (
                  <div key={item.id} className="border-b border-white/[0.06] pb-2 mb-1">
                    <div className="text-white/50 text-xs uppercase tracking-wider py-2">{item.label}</div>
                    <div className="pl-3 space-y-1">
                      {children.map((child) => (
                        <a
                          key={child.id}
                          href={child.href}
                          data-testid={`navbar-${child.id}-mobile`}
                          onClick={() => setMobileMenuOpen(false)}
                          className={mobileNavLinkClass(child.style)}
                        >
                          {child.label}
                        </a>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <a
                  key={item.id}
                  href={item.href}
                  data-testid={`navbar-${item.id}-mobile`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={mobileNavLinkClass(item.style)}
                >
                  {item.label}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

// ============== MAIN APP ==============
const Home = () => {
  const [services, setServices] = useState([]);
  const [businessHours, setBusinessHours] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [showBanner, setShowBanner] = useState(true);
  const [siteBannerOn, setSiteBannerOn] = useState(false);
  const [googleReviews, setGoogleReviews] = useState({ rating: 4.8, user_ratings_total: 88, url: "https://www.google.com/maps/search/Auvergne+Tech+Racing+Clermont-Ferrand", place_id: "" });
  const contact = useContactInfo();
  const [homeContent, setHomeContent] = useState(null);
  const [reviewNudge, setReviewNudge] = useState(null);

  // Per-page SEO — CMS values (home_landing.seo) win over hardcoded fallbacks so admins
  // can rename the page from the Content tab without touching code.
  const cmsSeo = homeContent?.seo || {};
  useSiteMeta({
    title: cmsSeo.title || "ATR — Flexfuel E85, Reprog Moteur & Mécanique Clermont",
    description: cmsSeo.description || "Spécialiste conversion Flexfuel E85, reprogrammation moteur Stage 1 & 2 et mécanique véhicules allemands à Clermont-Ferrand. Devis gratuit au 06 49 07 08 20.",
    canonical_path: "/",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, hoursRes, promosRes, galleryRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/business-hours`),
          axios.get(`${API}/promotions`),
          axios.get(`${API}/gallery`),
        ]);
        setServices(servicesRes.data);
        setBusinessHours(hoursRes.data);
        const todayParis = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });
        setPromotions(promosRes.data.filter((p) => !p.valid_until || p.valid_until.slice(0, 10) >= todayParis));
        setGalleryImages(galleryRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Erreur de connexion au serveur");
      }
    };

    fetchData();

    // Live Google rating/review count — refetch on window focus + every 5 min
    // so new reviews show up quickly without a full page reload.
    const fetchGoogleReviews = (force = false) => {
      const url = `${API}/google-reviews${force ? "?force=1" : ""}`;
      axios.get(url)
        .then(r => setGoogleReviews({
          rating: r.data.rating ?? 4.8,
          user_ratings_total: r.data.user_ratings_total ?? 88,
          url: r.data.url || "https://www.google.com/maps/search/Auvergne+Tech+Racing+Clermont-Ferrand",
          place_id: r.data.place_id || "",
        }))
        .catch(() => {});
    };
    fetchGoogleReviews();

    // Refetch when the user comes back to the tab (force = bypass 5 min server cache)
    const onFocus = () => fetchGoogleReviews(true);
    window.addEventListener("focus", onFocus);

    // Periodic refresh every 5 min while the tab stays open
    const intervalId = setInterval(() => fetchGoogleReviews(false), 5 * 60 * 1000);

    // Fetch editable home landing content (non-blocking, falls back to hardcoded defaults)
    axios.get(`${API}/content/home_landing`)
      .then(r => setHomeContent(r.data))
      .catch(() => {});

    // Fetch editable review-nudge config (delay, cooldown, texts, enabled)
    axios.get(`${API}/content/review_nudge`)
      .then(r => setReviewNudge(r.data))
      .catch(() => {});

    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(intervalId);
    };
  }, []);

  // Scroll to the element matching window.location.hash after Home mounts.
  // Handles inbound navigations like `/#booking` from Navbar or FlexfuelPage
  // where React Router / anchor tags don't natively scroll to hash targets.
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    // Poll briefly because sections mount after async CMS fetches
    let tries = 0;
    const maxTries = 30; // ~4.5s total
    const t = setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        clearInterval(t);
      } else if (++tries >= maxTries) {
        clearInterval(t);
      }
    }, 150);
    return () => clearInterval(t);
  }, [services.length, homeContent]);

  const scrollToBooking = () => {
    document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
  };

  // LocalBusiness JSON-LD schema (with live rating)
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    "@id": "https://auvergnetechracing.com/#business",
    name: contact.business_name,
    alternateName: "ATR",
    image: "https://auvergnetechracing.com/logo.png",
    logo: "https://auvergnetechracing.com/logo-512.png",
    url: "https://auvergnetechracing.com",
    telephone: contact.phone,
    email: contact.email,
    priceRange: "€€",
    address: {
      "@type": "PostalAddress",
      streetAddress: contact.street,
      postalCode: contact.postal_code,
      addressLocality: contact.city,
      addressCountry: contact.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 45.7772,
      longitude: 3.0870,
    },
    openingHoursSpecification: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday"], opens: "08:00", closes: "12:30" },
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday"], opens: "13:30", closes: "18:00" },
      { "@type": "OpeningHoursSpecification", dayOfWeek: "Friday", opens: "08:00", closes: "12:30" },
      { "@type": "OpeningHoursSpecification", dayOfWeek: "Friday", opens: "14:30", closes: "18:00" },
    ],
    sameAs: [
      contact.maps_url,
      "https://www.facebook.com/auvergnetechracing",
      "https://www.instagram.com/auvergnetechracing",
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: googleReviews.rating,
      reviewCount: googleReviews.user_ratings_total,
      bestRating: 5,
      worstRating: 1,
    },
    areaServed: { "@type": "City", name: contact.city },
    knowsAbout: [
      "Conversion Flexfuel E85",
      "Reprogrammation moteur",
      "Entretien automobile",
      "Freinage",
      "Pneumatiques",
    ],
  };

  const showPromoBanner = showBanner && !siteBannerOn && promotions.some((p) => p.show_banner && p.is_active);

  const homeSectionEls = {
    animation: (
      <section className="py-12 md:py-16 bg-[#0A0A0A] border-t border-white/[0.06]" data-testid="home-animation-section">
        <div className="max-w-5xl mx-auto px-6">
          <AnimationEmbed src="/accueil.html" title="Auvergne Tech Racing — Garage Clermont-Ferrand" testId="accueil-animation" />
        </div>
      </section>
    ),
    promotions: <PromotionsSection promotions={promotions} content={homeContent} />,
    services: <ServicesSection services={services} selectedService={selectedService} onSelectService={setSelectedService} content={homeContent} />,
    vehicle: <VehicleSelectorWidget />,
    flexfuel_calculator: <FlexfuelCalculator content={homeContent} />,
    flexfuel_faq: <FlexfuelFAQSection content={homeContent} />,
    gallery: <GallerySection images={galleryImages} />,
    marquee: <MarqueeSection />,
    google_reviews: <GoogleReviewsSection reviews={googleReviews} content={homeContent} />,
    testimonials: <TestimonialsSection content={homeContent} />,
    seo_editorial: <SEOEditorialSection reviews={googleReviews} content={homeContent} />,
    booking: <BookingSection services={services} selectedService={selectedService} onSelectService={setSelectedService} content={homeContent} />,
  };

  return (
    <div data-testid="home-page">
      <script
        type="application/ld+json"
        data-testid="localbusiness-jsonld"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      <Toaster position="top-right" richColors />
      {showPromoBanner && (
        <PromoBanner promotions={promotions} onClose={() => setShowBanner(false)} />
      )}
      <Navbar onBookClick={scrollToBooking} hasBanner={showPromoBanner} onSiteBannerChange={setSiteBannerOn} />
      <HeroSection onBookClick={scrollToBooking} reviews={googleReviews} content={homeContent} />
      {resolveHomeSections(homeContent?.sections)
        .filter((s) => s.visible)
        .map((s) => <Fragment key={s.id}>{homeSectionEls[s.id]}</Fragment>)}
      <Footer businessHours={businessHours} content={homeContent} />
      <SocialMediaButtons />
      <ReviewNudgeToast placeId={googleReviews.place_id} businessUrl={googleReviews.url} config={reviewNudge} />
    </div>
  );
};

// ============== CLIENT APPOINTMENT MANAGEMENT PAGE ==============
const AppointmentManagement = () => {
  const { token } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const contact = useContactInfo();

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        const response = await axios.get(`${API}/appointments/client/${token}`);
        setAppointment(response.data);
      } catch (err) {
        setError("Rendez-vous non trouvé ou lien expiré");
      } finally {
        setLoading(false);
      }
    };
    fetchAppointment();
  }, [token]);

  const handleCancel = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler ce rendez-vous ?")) {
      return;
    }
    setCancelling(true);
    try {
      await axios.post(`${API}/appointments/client/${token}/cancel`);
      setCancelled(true);
      toast.success("Rendez-vous annulé avec succès");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
    const months = [
      "janvier", "février", "mars", "avril", "mai", "juin",
      "juillet", "août", "septembre", "octobre", "novembre", "décembre"
    ];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="bg-[#121212] border border-white/10 p-8 rounded-lg text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-white text-xl font-bold mb-2">Oups !</h1>
          <p className="text-white/60">{error}</p>
          <a href="/" className="inline-block mt-6 bg-[#FF3B30] text-white px-6 py-3 rounded font-bold">
            Retour à l'accueil
          </a>
        </div>
      </div>
    );
  }

  const isCancelled = appointment.status === "cancelled" || cancelled;

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
      <Toaster position="top-right" richColors />
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-black tracking-tighter uppercase italic text-white">
            AUVERGNE<span className="text-[#FF3B30]"> TECH RACING</span>
          </h1>
          <p className="text-white/60 mt-2">Gestion de votre rendez-vous</p>
        </div>

        {/* Appointment Card */}
        <div className={`bg-[#121212] border rounded-lg overflow-hidden ${isCancelled ? 'border-red-500/50' : 'border-white/10'}`}>
          {isCancelled && (
            <div className="bg-red-500/20 text-red-400 py-3 px-4 text-center font-bold">
              ❌ Ce rendez-vous a été annulé
            </div>
          )}

          <div className="p-6">
            {/* Service */}
            <div className="mb-6">
              <p className="text-white/60 text-sm mb-1">Service</p>
              <p className="text-white text-xl font-bold">{appointment.service_name}</p>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-white/60 text-sm mb-1">Date</p>
                <p className="text-white font-medium">{formatDate(appointment.date)}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">Heure</p>
                <p className="text-white font-medium text-xl">{appointment.time}</p>
              </div>
            </div>

            {/* Vehicle */}
            {appointment.client_plate && (
              <div className="mb-6">
                <p className="text-white/60 text-sm mb-1">Véhicule</p>
                <p className="text-[#FF3B30] text-xl font-bold">{appointment.client_plate}</p>
              </div>
            )}

            {/* Status */}
            <div className="mb-6">
              <p className="text-white/60 text-sm mb-1">Statut</p>
              <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${
                isCancelled 
                  ? 'bg-red-500/20 text-red-400'
                  : appointment.status === 'confirmed'
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {isCancelled ? 'Annulé' : appointment.status === 'confirmed' ? 'Confirmé' : 'En attente'}
              </span>
            </div>

            {/* Address */}
            <div className="bg-[#1C1C1E] p-4 rounded mb-6">
              <p className="text-white/60 text-sm mb-2">📍 Adresse</p>
              <p className="text-white">
                {contact.street}<br />
                {contact.postal_code} {contact.city}
              </p>
              <p className="text-[#FF3B30] mt-2">📞 {contact.phone_display}</p>
            </div>

            {/* Actions */}
            {!isCancelled && (
              <div className="space-y-3">
                <a
                  href={`https://wa.me/${contact.phone.replace(/^\+/, "")}?text=Bonjour, je souhaite modifier mon rendez-vous`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-[#25D366] hover:bg-[#20BA5C] text-white py-3 px-4 rounded font-bold text-center transition-colors"
                >
                  💬 Contacter pour modifier
                </a>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 py-3 px-4 rounded font-bold transition-colors disabled:opacity-50"
                >
                  {cancelling ? "Annulation..." : "❌ Annuler ce rendez-vous"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-6">
          <a href="/" className="text-white/50 hover:text-white transition-colors">
            ← Retour au site
          </a>
        </div>
      </div>
    </div>
  );
};

function App() {
  // useSiteMeta is now called per-page (Home, Shop, FlexfuelPage, FileService)
  // with route-specific title/description/canonical overrides for real per-page SEO.
  useEffect(() => {
    checkQuotePayment();
  }, []);
  
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/conversion-flexfuel-e85" element={<FlexfuelPage />} />
          <Route path="/reprogrammation-moteur-clermont-ferrand" element={<ReprogPage />} />
          <Route path="/achat-vente-voiture-clermont-ferrand" element={<AchatVentePage />} />
          <Route path="/depot-vente/fiche/:id" element={<PrintableVehiclePage />} />
          <Route path="/reparation-entretien-clermont-ferrand" element={<RepEntretienPage />} />
          <Route path="/depot-vente-tech" element={<DepotVenteTech />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/success" element={<Shop />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/atelier" element={<Atelier />} />
          <Route path="/file-service" element={<FileService />} />
          <Route path="/rdv/:token" element={<AppointmentManagement />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
